import { useQuery } from '@apollo/client';
import { GET_CAR_BOOKED_DATES } from '../services/carService/queries';
import { useMemo } from 'react';
import { slotsFromGraphQl, type BookedSlot } from '../helpers/bookedSlots';

interface IGetCarBookedDatesQuery {
  getCarBookedDates?: {
    bookedTimeSlots?: Array<{
      startAt: number;
      endAt: number;
    }>;
    tripBufferHours?: number;
  };
}

interface IGetCarBookedDatesQueryVariables {
  carId: string;
  excludeBookingId?: string;
}

export const useGetCarBlackoutDaysHook = ({
  carId,
  excludeBookingId,
  priceCalendar,
  timezone,
}: {
  carId: string | undefined;
  excludeBookingId: string | null;
  priceCalendar?: Array<{ date: string | number | Date; isAvailable?: boolean | null }> | null;
  timezone?: string | null;
}) => {
  const {
    data,
    loading: loadingBlackoutDays,
    error: errorBlackoutDays,
  } = useQuery<IGetCarBookedDatesQuery, IGetCarBookedDatesQueryVariables>(GET_CAR_BOOKED_DATES, {
    variables: { carId: carId || '', excludeBookingId: excludeBookingId || undefined },
    skip: !carId,
    fetchPolicy: 'cache-and-network',
  });

  const {
    bookedDays,
    bookedSlots,
    tripBufferHours,
    blackoutDays,
    blackoutDayKeys,
    unavailableDays,
    resolvedTimezone,
  } = useMemo(() => {
    const bookedSlots: BookedSlot[] = slotsFromGraphQl(data?.getCarBookedDates?.bookedTimeSlots);
    const bookedDays = bookedSlots.map(slot => ({
      from: new Date(slot.startAt),
      to: new Date(slot.endAt),
    }));
    // Server default is 3h; a stored 0 means no buffer.
    const tripBufferHours = data?.getCarBookedDates?.tripBufferHours ?? 3;

    // Price-calendar days switched off by the host, as `yyyy-MM-dd` keys.
    const blackoutDayKeys = new Set<string>(
      (priceCalendar ?? [])
        .filter(day => day?.isAvailable === false)
        .map(day =>
          (day.date instanceof Date ? day.date.toISOString() : String(day.date)).slice(0, 10),
        ),
    );
    const blackoutDays = Array.from(blackoutDayKeys).map(key => {
      const [y, m, d] = key.split('-').map(Number);
      return new Date(y, m - 1, d);
    });

    // `getCarBookedDates` already merges booking blocks and host availability blocks.
    const unavailableDays: Array<{ from: Date; to: Date }> = [];
    const resolvedTimezone = timezone || 'America/Los_Angeles';

    return {
      bookedDays,
      bookedSlots,
      tripBufferHours,
      blackoutDays,
      blackoutDayKeys,
      unavailableDays,
      resolvedTimezone,
    };
  }, [data, priceCalendar, timezone]);

  return {
    blackoutDays,
    blackoutDayKeys,
    bookedDays,
    bookedSlots,
    tripBufferHours,
    unavailableDays,
    timezone: resolvedTimezone,
    loading: loadingBlackoutDays,
    error: errorBlackoutDays,
  };
};
